import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense } from 'react';
import { auth } from './firebase';
import { db } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { snapshotMigrationConfig } from './utils/migrationConfig';
import { createAppeal as apiCreateAppeal, apiErrorReason } from './api/users';
import { ApiError } from './api/client';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';
import { CartProvider } from './context/CartContext';
import { Ban, AlertTriangle } from 'lucide-react';
import { TopBarProvider } from './context/TopBarContext';
import BottomNav from './components/BottomNav';
import GlobalAnnouncement from './components/GlobalAnnouncement';
import OfflineIndicator from './components/OfflineIndicator';
import PushPrompt from './components/PushPrompt';
import VerifyEmailBanner from './components/VerifyEmailBanner';
import MobileTopBar from './components/MobileTopBar';
import AssistantFab from './components/AssistantFab';
import Sidebar from './components/Sidebar';
import AmbientFX from './components/AmbientFX';
import ErrorBoundary from './components/ErrorBoundary';
import Skeleton from './components/Skeleton';
import LaunchGate from './components/LaunchGate';
import TwoFactorGate from './components/TwoFactorGate';
import ProfileGate from './components/ProfileGate';
import EntradaGate from './components/EntradaGate';
import LoadFailed from './components/LoadFailed';
import { useIsDesktop } from './hooks/useIsDesktop';
import { trackScreenView } from './utils/analytics';
import { isPublicPath } from './utils/publicRoutes';
import { zLayer } from './styles/zIndex';
import { dsv2, dsv2Light } from './styles/designSystemV2';
import { useThemeColors } from './context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { EMERGENCIAS_ACTIVAS } from './utils/featureFlags';
import { guardarDestino, tomarDestino } from './utils/returnTo';
import './styles/darkMode.css';

// ── Lazy-loaded pages ────────────────────────────────────────────────────────
const Landing            = lazy(() => import('./pages/Landing'));
const Login              = lazy(() => import('./pages/Login'));
const ResetPassword      = lazy(() => import('./pages/ResetPassword'));
const Home               = lazy(() => import('./pages/Home'));
const Inbox              = lazy(() => import('./pages/Inbox'));
const Vehicles           = lazy(() => import('./pages/Vehicles'));
const VehicleForm        = lazy(() => import('./pages/VehicleForm'));
const VehicleDetail      = lazy(() => import('./pages/VehicleDetail'));
const Diagnose           = lazy(() => import('./pages/Diagnose'));
const DiagnoseHub        = lazy(() => import('./pages/DiagnoseHub'));
const Suggestions        = lazy(() => import('./pages/Suggestions'));
const MechanicSetup      = lazy(() => import('./pages/MechanicSetup'));
const MechanicDashboard  = lazy(() => import('./pages/MechanicDashboard'));
const MechanicsList      = lazy(() => import('./pages/MechanicsList'));
const MechanicProfile    = lazy(() => import('./pages/MechanicProfile'));
const AppointmentRequest = lazy(() => import('./pages/AppointmentRequest'));
const MyAppointments     = lazy(() => import('./pages/MyAppointments'));
const MechanicAppointments = lazy(() => import('./pages/MechanicAppointments'));
const MechanicAddService = lazy(() => import('./pages/MechanicAddService'));
const MechanicBilling    = lazy(() => import('./pages/MechanicBilling'));
const ClientProBilling   = lazy(() => import('./pages/ClientProBilling'));
const MechanicServiceHistory = lazy(() => import('./pages/MechanicServiceHistory'));
const AdminDashboard     = lazy(() => import('./pages/AdminDashboard'));
const AdminUserDetail    = lazy(() => import('./pages/AdminUserDetail'));
const AdminKyc           = lazy(() => import('./pages/AdminKyc'));
const AdminAppeals       = lazy(() => import('./pages/AdminAppeals'));
const AdminLaunch        = lazy(() => import('./pages/AdminLaunch'));
const AdminReviews       = lazy(() => import('./pages/AdminReviews'));
const AdminAudit         = lazy(() => import('./pages/AdminAudit'));
const AdminCensus        = lazy(() => import('./pages/AdminCensus'));
const AdminMigraciones   = lazy(() => import('./pages/AdminMigraciones'));
const AdminEmergencies   = lazy(() => import('./pages/AdminEmergencies'));
const AdminOrders        = lazy(() => import('./pages/AdminOrders'));
const AdminSupport       = lazy(() => import('./pages/AdminSupport'));
const AdminSuggestions   = lazy(() => import('./pages/AdminSuggestions'));
const AdminAnalytics     = lazy(() => import('./pages/AdminAnalytics'));
const AdminProductsMod   = lazy(() => import('./pages/AdminProductsMod'));
const AdminAnnouncements = lazy(() => import('./pages/AdminAnnouncements'));
const ClientDashboard    = lazy(() => import('./pages/ClientDashboard'));
const MechanicsMap       = lazy(() => import('./pages/MechanicsMap'));
const ShareVehicle       = lazy(() => import('./pages/ShareVehicle'));
const ProfilePage        = lazy(() => import('./pages/ProfilePage'));
const EditProfile        = lazy(() => import('./pages/EditProfile'));
const WorkshopSetup      = lazy(() => import('./pages/WorkshopSetup'));
const WorkshopDashboard  = lazy(() => import('./pages/WorkshopDashboard'));
const WorkshopCalendar   = lazy(() => import('./pages/WorkshopCalendar'));
const WorkshopCrm        = lazy(() => import('./pages/WorkshopCrm'));
const WorkshopVerification = lazy(() => import('./pages/WorkshopVerification'));
const AdminWorkshopKyc   = lazy(() => import('./pages/AdminWorkshopKyc'));
const AdminWorkshopApprovals = lazy(() => import('./pages/AdminWorkshopApprovals'));
const WorkshopAppointments = lazy(() => import('./pages/WorkshopAppointments'));
const WorkshopReviews    = lazy(() => import('./pages/WorkshopReviews'));
const WorkshopProducts   = lazy(() => import('./pages/WorkshopProducts'));
const WorkshopStore      = lazy(() => import('./pages/WorkshopStore'));
const Marketplace        = lazy(() => import('./pages/Marketplace'));
const ProductDetail      = lazy(() => import('./pages/ProductDetail'));
const Cart               = lazy(() => import('./pages/Cart'));
const MyOrders           = lazy(() => import('./pages/MyOrders'));
const Checkout           = lazy(() => import('./pages/Checkout'));
const WorkshopOrders     = lazy(() => import('./pages/WorkshopOrders'));
const WorkshopEarnings   = lazy(() => import('./pages/WorkshopEarnings'));
const WorkshopBilling     = lazy(() => import('./pages/WorkshopBilling'));
const WorkshopProfileViews = lazy(() => import('./pages/WorkshopProfileViews'));
const WorkshopClients     = lazy(() => import('./pages/WorkshopClients'));
const Help               = lazy(() => import('./pages/Help'));
const HelpCase           = lazy(() => import('./pages/HelpCase'));
const BusinessCases      = lazy(() => import('./pages/BusinessCases'));
const Messages           = lazy(() => import('./pages/Messages'));
const ChatConversation   = lazy(() => import('./pages/ChatConversation'));
const Privacy            = lazy(() => import('./pages/Privacy'));
const Terms              = lazy(() => import('./pages/Terms'));
const PaymentSecurity    = lazy(() => import('./pages/PaymentSecurity'));
const Refunds            = lazy(() => import('./pages/Refunds'));
const Delivery           = lazy(() => import('./pages/Delivery'));
const Contact            = lazy(() => import('./pages/Contact'));
const Rankings           = lazy(() => import('./pages/Rankings'));
const WorkshopProfile    = lazy(() => import('./pages/WorkshopProfile'));
const Pricing            = lazy(() => import('./pages/Pricing'));
const Asistencia         = lazy(() => import('./pages/Asistencia'));
const EmergencyRequest   = lazy(() => import('./pages/EmergencyRequest'));
const ProviderMode       = lazy(() => import('./pages/ProviderMode'));
const AutosList          = lazy(() => import('./pages/AutosList'));
const AutoDetail         = lazy(() => import('./pages/AutoDetail'));
const SellWizard         = lazy(() => import('./pages/SellWizard'));
const MyListings         = lazy(() => import('./pages/MyListings'));
const InspectionForm     = lazy(() => import('./pages/InspectionForm'));
const WorkshopInspections = lazy(() => import('./pages/WorkshopInspections'));
const WorkshopRegisterService = lazy(() => import('./pages/WorkshopRegisterService'));
const NotFound           = lazy(() => import('./pages/NotFound'));


// ── Fallback mientras carga un chunk ────────────────────────────────────────
// Fondo según el tema (evita el flash blanco en dark) y spinner con retraso de
// 300ms: si el chunk carga rápido no se ve nada y la transición se siente fluida.
function PageLoader() {
  const isDark = typeof document !== 'undefined' && document.body.getAttribute('data-theme') === 'dark';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', flexDirection: 'column', gap: '16px',
      backgroundColor: isDark ? '#101416' : '#F7F9FB'
    }}>
      <div style={{
        width: '40px', height: '40px',
        border: '3px solid #DC262620', borderTop: '3px solid #DC2626',
        borderRadius: '50%', opacity: 0,
        animation: 'spin 1s linear infinite, plShow .2s ease .3s 1 forwards',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } @keyframes plShow { to { opacity: 1; } }`}</style>
    </div>
  );
}

/**
 * LA CÁSCARA DEL ARRANQUE — lo que se ve mientras llega el perfil.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ ESTO NO ES LA APP: ES LA SALA DE ESPERA. AQUÍ NO ENTRA NI UNA RUTA.       ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * Medido en producción (móvil, 4G lenta): la primera visita a /home tardaba
 * ~5 s en enseñar los carros y los primeros 2,5 s eran una pantalla EN BLANCO,
 * porque `SuspensionGate` devolvía un spinner hasta que contestaba el servidor.
 *
 * Lo que cambia es QUÉ se enseña en esa espera, no CUÁNTO dura ni QUIÉN pasa:
 *
 *  · Se pinta la estructura de la pantalla (cabecera, siluetas del contenido) y
 *    el saludo con el nombre y la foto de la visita anterior. Eso es todo lo que
 *    guarda `cascara` (ver el recuadro de AuthContext.js): identidad cosmética,
 *    sin rol y sin sanción.
 *  · NO se pinta ni una sola ruta, ni el menú, ni nada que dependa de `role` o
 *    de `suspended`. El gate sigue cerrado exactamente igual que antes.
 *
 * Si no hay cáscara (primera vez, otro aparato, modo privado, plazo vencido) se
 * enseña lo mismo sin nombre: siluetas. Nunca un nombre que no sea el de la
 * sesión que está abierta ahora mismo.
 */
function ArranqueShell() {
  const { cascara, user, userProfile } = useAuth();
  const { t } = useTranslation();
  const { isDark } = useThemeColors();
  const tokens = isDark ? dsv2 : dsv2Light;

  // Se pinta en DOS momentos del arranque y tiene que decir lo mismo en los dos:
  // antes de que llegue el perfil (solo hay cáscara) y mientras bajan los chunks
  // de la ruta (el perfil YA está). Por eso el perfil manda cuando existe y la
  // cáscara sirve de respaldo; si dijeran cosas distintas, el saludo cambiaría a
  // media espera. `user` es el objeto de Firebase de ESTA sesión, así que su
  // `displayName` no puede ser el de otra cuenta: es el último respaldo para
  // quien todavía no tiene cáscara guardada.
  const nombre = String(userProfile?.fullName || cascara?.nombre || user?.displayName || '').trim().split(' ')[0];
  const foto = userProfile?.photoURL || cascara?.foto || user?.photoURL || null;
  const inicial = (nombre || 'M').charAt(0).toUpperCase();

  const hora = new Date().getHours();
  const saludo = hora < 12 ? t('home.goodMorning') : hora < 18 ? t('home.goodAfternoon') : t('home.goodEvening');

  return (
    <div style={{ minHeight: '100vh', background: tokens.bg, padding: '20px 16px' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          {foto ? (
            <img
              src={foto}
              alt=""
              style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
            />
          ) : (
            <div style={{
              width: '44px', height: '44px', borderRadius: '50%', flexShrink: 0,
              background: '#DC2626', color: 'white', fontWeight: 700, fontSize: '18px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>{inicial}</div>
          )}
          <div style={{ minWidth: 0 }}>
            {nombre ? (
              <p style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: tokens.text }}>
                {saludo}, {nombre}
              </p>
            ) : (
              <Skeleton w={160} h={18} />
            )}
            <div style={{ marginTop: '8px' }}><Skeleton w={110} h={12} /></div>
          </div>
        </div>

        <Skeleton h={132} r={16} style={{ marginBottom: '16px' }} />
        <Skeleton h={76} r={14} style={{ marginBottom: '12px' }} />
        <Skeleton h={76} r={14} style={{ marginBottom: '12px' }} />
        <Skeleton h={76} r={14} />
      </div>
    </div>
  );
}

// ─── EL ARRANQUE NO PUEDE IR HACIA ATRÁS ────────────────────────────────────
//
// 🔴 Medido en producción tras estrenar la cáscara: la pantalla avanzaba y luego
// RETROCEDÍA. `spinner → saludo + siluetas → spinner OTRA VEZ a pantalla
// completa → contenido`, entre 306 y 666 ms de ese paso atrás según la red.
//
// La causa: en cuanto llega el perfil, `SuspensionGate` deja de pintar la
// cáscara y el relevo lo coge el `fallback` del `Suspense` mientras bajan los
// chunks de la ruta. Antes no se veía porque el gate y el Suspense enseñaban EL
// MISMO spinner y la espera parecía continua. Enseñar algo mejor y retirarlo es
// peor que no haberlo enseñado.
//
// Se arregla haciendo que el relevo sea la MISMA cáscara. Pero solo durante el
// arranque, y esto es la mitad importante:
//
// ⚠️ `PageLoader` no pinta nada durante sus primeros 300 ms (el spinner nace con
// `opacity: 0` y una animación retrasada), así que una navegación con el chunk
// ya en caché no enseña NADA. Poner la cáscara ahí sin más convertiría cada
// navegación en un parpadeo de siluetas —y de un saludo a media sesión, que no
// viene a cuento—. Un destello de contenido es peor que un destello de nada.
//
// Por eso: mientras no se haya pintado ninguna ruta en esta carga de página,
// el relevo es la cáscara (continuidad del arranque); después, el fallback
// vuelve a ser exactamente el de siempre y las navegaciones no cambian.
//
// Un chunk que no baja NO deja siluetas eternas: el `React.lazy` rechaza y lo
// recoge el `ErrorBoundary key={location.pathname}` que envuelve al `Suspense`.

// 🔴 CÓMO SE SABE QUE «YA SE PINTÓ UNA RUTA», Y POR QUÉ LA SEÑAL DE ANTES NO
// SERVÍA. La condición («solo mientras no se haya pintado ninguna ruta en esta
// carga») es la correcta; lo que fallaba era el testigo. Era un componente
// hermano de `<Routes>` con un `useEffect`, apoyado en que «si los hijos se
// suspenden no llega a montarse». Medido en Chrome contra el build real, eso no
// se sostiene: basta con que la ruta que toca renderice un `<Navigate>` —una
// redirección, que no enseña NADA— para que el hermano se monte, su efecto
// corra y el relevo del arranque quede desarmado antes de que el cliente haya
// visto un solo píxel de la app. Reproducido entrando en frío a `/vehicles` sin
// sesión: la espera del chunk de Login se hacía con `PageLoader`, que sus
// primeros 300 ms no pinta nada, en vez de con las siluetas.
//
// El testigo nuevo no pregunta por el ciclo de vida de React, pregunta por el
// DOM: el contenedor de rutas llegó a TENER CONTENIDO. Una redirección deja el
// contenedor vacío y no cuenta; una pantalla de verdad sí. Es exactamente lo
// que la condición quiere decir —«el cliente ya vio una pantalla»— y no un
// indicio del que se dedujera.
let rutaPintada = false;

/**
 * Ref del contenedor de rutas: se anota SOLO si al montarlo hay contenido
 * dentro. React adjunta las refs después de haber creado los hijos en el DOM,
 * así que `childElementCount` ya es el definitivo de ese commit.
 */
function marcarRutaPintada(nodo) {
  if (nodo && nodo.childElementCount > 0) rutaPintada = true;
}

function FallbackDeRuta() {
  return rutaPintada ? <PageLoader /> : <ArranqueShell />;
}

/** Shows suspension banners / gates based on userProfile.suspended */
function SuspensionGate({ children }) {
  const { userProfile, user, profileLoaded } = useAuth();
  const { t } = useTranslation();
  const [appealText, setAppealText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [appealDone, setAppealDone] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const suspended = userProfile?.suspended;

  const handleAppeal = async () => {
    if (!appealText.trim() || !userProfile?.id) return;
    setSubmitting(true);
    try {
      const snap = await snapshotMigrationConfig();
      if (snap.readsFrom('users') === 'postgres') {
        // Apelar es el ÚNICO trámite que la guardia del API le deja hacer a un
        // suspendido total (`allowSuspended` en la ruta). Si le cerráramos esta
        // puerta la sanción pasaría a ser definitiva y sin revisión.
        //
        // El 409 es un ÉXITO aquí: significa "ya tienes una apelación pendiente
        // de ese tipo", que es exactamente lo que esta pantalla quiere confirmar.
        try {
          await apiCreateAppeal({ kind: 'SUSPENSION', text: appealText.trim() });
        } catch (err) {
          if (!(err instanceof ApiError && (err.status === 409 || err.code === 'conflict'))) throw err;
        }
      } else {
        await updateDoc(doc(db, 'users', userProfile.id), {
          suspensionAppeal: {
            text: appealText.trim(),
            submittedAt: serverTimestamp(),
            status: 'pending'
          }
        });
      }
      setAppealDone(true);
    } catch (e) {
      // Esta pantalla no tiene dónde enseñar un error (no hay toast dentro del
      // gate), así que el motivo real va a la consola en vez del objeto pelado.
      // ⚠️ El API exige 10 caracteres mínimo y esta caja acepta 1: un texto
      // corto se rechaza y el usuario no ve por qué. Anotado en el informe.
      console.error('[suspension] no se pudo presentar la apelación:', apiErrorReason(e) || e?.message || e);
    }
    setSubmitting(false);
  };

  const handleLogout = async () => {
    await signOut(auth);
    window.location.href = '/';
  };

  // Already appealed before this session?
  const alreadyAppealed = userProfile?.suspensionAppeal?.status === 'pending' ||
    userProfile?.suspensionAppeal?.status === 'reviewed';

  // No mostrar contenido protegido hasta saber si el usuario está suspendido.
  // AppRoutes baja su propio `loading` apenas resuelve el Auth (sin esperar el
  // perfil), así que sin esto un usuario suspendido veía la app durante la carga
  // del perfil. profileLoaded cubre también el doc inexistente (no bucle infinito).
  //
  // 🔴 LA CONDICIÓN NO SE TOCA, Y NO SE PUEDE TOCAR. Lo que cambió es que en vez
  // de un spinner en blanco se enseña `ArranqueShell`, que es cáscara y nada
  // más: ni una ruta, ni el menú, ni nada que dependa de `role` ni de
  // `suspended`. Añadir aquí un `|| hayCascara` para dejar pasar a `children`
  // sería exactamente el agujero que este gate existe para cerrar: el usuario al
  // que suspendieron entre su última visita y ahora entraría durante el medio
  // segundo que tarda el perfil, y a quien le cambiaron el rol vería la app
  // equivocada. Medio segundo de app abierta es una sanción que no sanciona.
  if (user && !profileLoaded) {
    return <ArranqueShell />;
  }

  if (suspended === 'total') {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px', overflowY: 'auto'
      }}>
        <div style={{
          background: 'white', borderRadius: '20px', padding: '36px 28px',
          maxWidth: '480px', width: '100%', textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
        }}>
          <Ban size={48} color="#dc2626" style={{ marginBottom: '12px' }} />
          <h2 style={{ margin: '0 0 8px', fontSize: '22px', color: '#111827' }}>
            {t('suspension.totalTitle')}
          </h2>
          {userProfile?.suspensionReason && (
            <div style={{
              background: '#FEF2F2', border: '1px solid #FECACA',
              borderRadius: '10px', padding: '12px 16px', margin: '16px 0', textAlign: 'left'
            }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#6B7280' }}>{t('suspension.reason')}</p>
              <p style={{ margin: '4px 0 0', fontSize: '15px', color: '#111827' }}>
                {userProfile.suspensionReason}
              </p>
            </div>
          )}
          <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 20px' }}>
            {t('suspension.totalInfo')}
          </p>
          {(alreadyAppealed || appealDone) ? (
            <div style={{
              background: '#F0FDF4', border: '1px solid #BBF7D0',
              borderRadius: '10px', padding: '12px 16px', margin: '0 0 20px'
            }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#166534' }}>
                {t('suspension.appealPending')}
              </p>
            </div>
          ) : (
            <div style={{ textAlign: 'left', marginBottom: '20px' }}>
              <p style={{ fontSize: '14px', color: '#374151', margin: '0 0 8px' }}>
                {t('suspension.appealPrompt')}
              </p>
              <textarea
                value={appealText}
                onChange={e => setAppealText(e.target.value)}
                placeholder={t('suspension.appealPlaceholder')}
                rows={3}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '10px',
                  border: '1.5px solid #D1D5DB', fontSize: '14px',
                  resize: 'none', boxSizing: 'border-box', outline: 'none'
                }}
              />
              <button
                onClick={handleAppeal}
                disabled={submitting || !appealText.trim()}
                style={{
                  marginTop: '8px', width: '100%', padding: '12px',
                  background: appealText.trim() ? '#7C3AED' : '#D1D5DB',
                  color: 'white', border: 'none', borderRadius: '10px',
                  fontWeight: '700', fontSize: '15px',
                  cursor: appealText.trim() ? 'pointer' : 'not-allowed'
                }}
              >
                {submitting ? '...' : t('suspension.submitAppeal')}
              </button>
            </div>
          )}
          <button
            onClick={handleLogout}
            style={{
              width: '100%', padding: '12px', background: '#F3F4F6',
              color: '#374151', border: 'none', borderRadius: '10px',
              fontWeight: '600', fontSize: '15px', cursor: 'pointer'
            }}
          >
            {t('suspension.logout')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {suspended === 'partial' && !dismissed && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: zLayer.suspensionBanner,
          background: 'linear-gradient(90deg, #7C3AED, #6D28D9)',
          padding: '12px 16px', color: 'white'
        }}>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <p style={{ margin: '0 0 2px', fontWeight: '700', fontSize: '14px' }}>
                  <AlertTriangle size={15} style={{ verticalAlign: '-2px', marginRight: 4 }} /> {t('suspension.partialTitle')}
                </p>
                <p style={{ margin: '0 0 4px', fontSize: '13px', opacity: 0.9 }}>
                  {t('suspension.partialInfo')}
                </p>
                {userProfile?.suspensionReason && (
                  <p style={{ margin: '0 0 8px', fontSize: '12px', opacity: 0.8 }}>
                    {t('suspension.reason')} {userProfile.suspensionReason}
                  </p>
                )}
                {(alreadyAppealed || appealDone) ? (
                  <p style={{ margin: 0, fontSize: '12px', opacity: 0.85, fontStyle: 'italic' }}>
                    {t('suspension.appealPending')}
                  </p>
                ) : (
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <input
                      type="text"
                      value={appealText}
                      onChange={e => setAppealText(e.target.value)}
                      placeholder={t('suspension.appealPlaceholder')}
                      style={{
                        flex: 1, padding: '6px 10px', borderRadius: '8px',
                        border: 'none', fontSize: '13px', outline: 'none'
                      }}
                    />
                    <button
                      onClick={handleAppeal}
                      disabled={submitting || !appealText.trim()}
                      style={{
                        padding: '6px 14px', background: 'white', color: '#7C3AED',
                        border: 'none', borderRadius: '8px', fontWeight: '700',
                        fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap'
                      }}
                    >
                      {submitting ? '...' : t('suspension.submitAppeal')}
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={() => setDismissed(true)}
                style={{
                  background: 'rgba(255,255,255,0.2)', border: 'none',
                  color: 'white', borderRadius: '6px', padding: '4px 8px',
                  cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap'
                }}
              >
                {t('suspension.dismiss')}
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={suspended === 'partial' && !dismissed ? { paddingTop: '90px' } : {}}>
        {children}
      </div>
    </>
  );
}

/** Redirige a /home si el usuario autenticado no es admin */
function RequireAdmin({ children }) {
  const { userProfile } = useAuth();
  if (!userProfile) return <PageLoader />;
  if (userProfile.role !== 'admin') return <Navigate to="/home" replace />;
  return children;
}

/**
 * Redirige a /home si el rol del perfil no está en `roles`. Mismo patrón que
 * RequireAdmin (redirección, no pantalla de error: la ruta simplemente no existe
 * para quien no le toca).
 *
 * Usa `realRole` y NO `effectiveRole` a propósito. El contexto activo es una
 * preferencia de VISTA guardada en localStorage: un dueño de taller que se pone
 * a mirar la app "como cliente" sigue siendo el dueño, y perdería el acceso a
 * sus propias pantallas al entrar por un enlace directo (el correo de alta de
 * taller apunta a /workshop/dashboard). La autorización mira quién eres, no con
 * qué lente estás mirando; la navegación (RoleHome) sí usa effectiveRole, que es
 * su sitio. Además `realRole` arrastra el respaldo legacy `userType`
 * (AuthContext.js:125), así que las cuentas viejas no se quedan fuera.
 *
 * Espera a `profileLoaded` en vez de a `userProfile != null`: si el doc del
 * usuario no existe, `userProfile` se queda en null para siempre y un
 * `if (!userProfile)` dejaría un spinner infinito.
 */
function RequireRole({ roles, children }) {
  const { realRole, profileLoaded } = useAuth();
  if (!profileLoaded) return <PageLoader />;
  if (!roles.includes(realRole)) return <Navigate to="/home" replace />;
  return children;
}

/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║ LA PUERTA DE LOS PUESTOS DE STAFF. TRES SALIDAS, NO DOS.                  ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 *
 * Una CAPACIDAD es un permiso suelto: quien la tiene sigue siendo `client` y solo
 * se le ha abierto una puerta concreta. Aprobar talleres o contestar apelaciones
 * ya no exige ser administrador, que era entregar el padrón entero.
 *
 * Se copia de `RequireRole` y NO de `RequireAdmin`: aquél espera a `profileLoaded`
 * y éste a `userProfile != null`, que con un documento inexistente deja un spinner
 * para siempre.
 *
 * ── LA TERCERA SALIDA, QUE ES LA RAZÓN DE QUE ESTO NO SEA UN `if` ───────────
 *
 * 🔴 `cargarPerfil` cae a Firestore ante cualquier fallo del API **o a los cinco
 * segundos** (`PERFIL_PLAZO_MS`), y un perfil de Firestore NO TRAE CAPACIDADES
 * por diseño. Con dos salidas —pasa o rebota— el staff acabaría en /home cada vez
 * que el servidor tardara: un rebote que depende de cómo iba la red ese segundo y
 * no de quién eres. Es el fallo que nadie consigue reproducir, y llegaría como
 * «a veces no me deja entrar».
 *
 * Así que «no vino del API» NO es «no le toca»: se pinta `LoadFailed`, con
 * reintento. La casa prohíbe convertir un fallo de carga en un estado vacío o en
 * un rebote mudo, y esto es lo mismo aplicado a una puerta.
 *
 * El admin pasa antes de todo eso, igual que `hasCapability` en el servidor: un
 * repliegue a Firestore no puede dejar al dueño fuera de sus propias colas.
 */
function RequireCapability({ capability, children }) {
  const { realRole, profileLoaded, perfilDesdeApi, tieneCapacidad, refreshProfile } = useAuth();
  const { isDark } = useThemeColors();
  if (!profileLoaded) return <PageLoader />;
  if (realRole === 'admin') return children;
  if (!perfilDesdeApi) {
    return (
      <div style={{ padding: '24px 16px', maxWidth: 720, margin: '0 auto' }}>
        <LoadFailed onRetry={refreshProfile} tokens={isDark ? dsv2 : dsv2Light} />
      </div>
    );
  }
  if (!tieneCapacidad(capability)) return <Navigate to="/home" replace />;
  return children;
}

// Gestión del taller: solo el dueño y el admin (que puede configurar un taller).
// OJO: el mecánico AFILIADO también lleva `workshopId` en su perfil, así que las
// páginas de taller, que consultan por `userProfile.workshopId`, le mostrarían
// los datos del NEGOCIO ajeno (catálogo y precios de distribuidor incluidos).
// Por eso el filtro es por rol y no por "tiene workshopId".
const WORKSHOP_ROLES = ['workshop_owner', 'admin'];

/** All roles can see the client home — professionals reach their dashboard via BottomNav */
function HomeGuard() {
  return <Home />;
}

/** Sin sesión: guarda la ruta a la que iba y manda a /login (al entrar se vuelve aquí). */
function LoginVolviendoAqui() {
  const location = useLocation();
  guardarDestino(location.pathname + location.search);
  return <Navigate to="/login" replace />;
}

/** Redirect logged-in users to their role's home (respeta el contexto activo) */
function RoleHome() {
  const { effectiveRole } = useAuth();
  const role = effectiveRole;
  // «A dónde iba» antes de que le pidieran sesión (ver utils/returnTo.js).
  const destino = tomarDestino();
  if (destino) return <Navigate to={destino} replace />;
  if (role === 'admin')          return <Navigate to="/admin/dashboard" replace />;
  if (role === 'mechanic')       return <Navigate to="/mechanic/dashboard" replace />;
  if (role === 'workshop_owner') return <Navigate to="/workshop/dashboard" replace />;
  return <Navigate to="/home" replace />;
}

/**
 * Layout que añade Sidebar fija a la izquierda en desktop para usuarios autenticados,
 * excluyendo rutas públicas y wizards de registro. Pass-through en móvil.
 *
 * Usa minHeight + position:sticky en lugar de height:100vh + overflow:hidden para no
 * cambiar el comportamiento de scroll de las páginas existentes.
 */
function AuthDesktopLayout({ user, children }) {
  const isDesktop = useIsDesktop();
  const location = useLocation();
  const { isDark } = useThemeColors();
  const tokens = isDark ? dsv2 : dsv2Light;

  const NO_SIDEBAR_PATHS = ['/workshop/setup', '/mechanic/setup'];

  const path = location.pathname;
  const sinSidebar = isPublicPath(path) || NO_SIDEBAR_PATHS.includes(path);

  const showSidebar = isDesktop && !!user && !sinSidebar;

  if (!showSidebar) return children;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `${tokens.space.sidebar}px 1fr`,
      minHeight: '100vh',
      alignItems: 'start',
      background: tokens.bg,
      position: 'relative',
    }}>
      <AmbientFX />
      <Sidebar />
      {/* SIN z-index: `position:relative` + `zIndex:1` creaba un contexto de
          apilamiento que encerraba TODOS los modales de página bajo z=1, pidieran lo
          que pidieran. Con z-index auto, main sigue pintándose sobre AmbientFX (que
          va antes en el DOM y también está posicionado) y los modales vuelven a subir. */}
      <main style={{ minWidth: 0, position: 'relative' }}>
        {children}
      </main>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  const isDesktop = useIsDesktop();
  const { userProfile } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Precarga en segundo plano (tras 2.5s de calma) los chunks de las rutas más
  // visitadas según el rol: la mayoría de las navegaciones ya no suspenden, así
  // que no hay spinner ni salto. webpack dedupe: repetir import() no descarga doble.
  useEffect(() => {
    if (!user) {
      // Sin sesión: precalentar solo el destino del login (Home) para que la
      // primera transición login → app no muestre spinner.
      const t = setTimeout(() => { Promise.allSettled([import('./pages/Home'), import('./pages/Login')]); }, 2000);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      const warm = [
        import('./pages/Home'), import('./pages/Vehicles'), import('./pages/VehicleDetail'),
        import('./pages/Marketplace'), import('./pages/MechanicsList'), import('./pages/MyAppointments'),
        import('./pages/ProfilePage'), import('./pages/EditProfile'), import('./pages/Inbox'),
        import('./pages/Asistencia'),
      ];
      const role = userProfile?.role || userProfile?.userType;
      if (role === 'workshop_owner') {
        warm.push(import('./pages/WorkshopDashboard'), import('./pages/WorkshopAppointments'),
          import('./pages/WorkshopOrders'), import('./pages/WorkshopProducts'));
      }
      if (role === 'mechanic') {
        warm.push(import('./pages/MechanicDashboard'), import('./pages/MechanicAppointments'));
      }
      Promise.allSettled(warm);
    }, 2500);
    return () => clearTimeout(t);
  }, [user, userProfile]);

  // Analítica de comportamiento: vista de pantalla (solo clientes). Deduplicado
  // por sesión dentro de trackScreenView → escritura de contador agregado barata.
  useEffect(() => {
    if (!user) return;
    const role = userProfile?.role || userProfile?.userType;
    if (role && role !== 'client') return;
    trackScreenView(location.pathname);
  }, [user, userProfile, location.pathname]);

  if (loading) return <PageLoader />;

  return (
    <SuspensionGate>
    <LaunchGate>
    <TwoFactorGate>
    <ProfileGate>
    <div style={{ paddingBottom: isDesktop ? 0 : '72px', minHeight: '100vh' }}>
      <OfflineIndicator />
      {user && <GlobalAnnouncement />}
      {/* El layout (sidebar desktop) vive FUERA del Suspense/ErrorBoundary: no se
          remonta al navegar y el fallback de carga solo reemplaza el contenido.
          El div .route-fade remonta por ruta (key del ErrorBoundary) → fade suave. */}
      <style>{`
        @keyframes routeFadeIn { from { opacity: 0; } to { opacity: 1; } }
        .route-fade { animation: routeFadeIn .18s ease-out; }
        @media (prefers-reduced-motion: reduce) { .route-fade { animation: none; } }
      `}</style>
      <AuthDesktopLayout user={user}>
        <ErrorBoundary key={location.pathname}>
        <Suspense fallback={<FallbackDeRuta />}>
          <div className="route-fade" ref={marcarRutaPintada}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<EntradaGate entrada={<Login />} home={<RoleHome />} cargando={<PageLoader />} />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* HOME */}
            <Route path="/home"  element={user ? <HomeGuard /> : <Navigate to="/login" />} />
            <Route path="/inbox" element={user ? <Inbox /> : <Navigate to="/login" />} />

            {/* VEHÍCULOS */}
            <Route path="/vehicles"                   element={user ? <Vehicles />    : <Navigate to="/login" />} />
            <Route path="/vehicles/new"               element={user ? <VehicleForm /> : <Navigate to="/login" />} />
            <Route path="/vehicles/:id/edit"          element={user ? <VehicleForm /> : <Navigate to="/login" />} />
            <Route path="/vehicle/:id"                element={user ? <VehicleDetail /> : <Navigate to="/login" />} />
            <Route path="/vehicle/:id/diagnose"       element={user ? <Diagnose />    : <Navigate to="/login" />} />
            <Route path="/diagnose"                   element={user ? <DiagnoseHub /> : <Navigate to="/login" />} />
            <Route path="/vehicle/:vehicleId/share"   element={user ? <ShareVehicle /> : <Navigate to="/login" />} />

            {/* PERFIL Y CONFIGURACIÓN */}
            <Route path="/dashboard"       element={user ? <ProfilePage />    : <Navigate to="/login" />} />
            <Route path="/dashboard/stats" element={user ? <ClientDashboard /> : <Navigate to="/login" />} />
            <Route path="/profile/edit"    element={user ? <EditProfile />    : <Navigate to="/login" />} />
            <Route path="/pro"             element={user ? <ClientProBilling /> : <Navigate to="/login" />} />

            {/* MECÁNICO */}
            <Route path="/mechanic/setup"       element={user ? <MechanicSetup />      : <Navigate to="/login" />} />
            <Route path="/mechanic/dashboard"   element={user ? <MechanicDashboard />  : <Navigate to="/login" />} />
            <Route path="/mechanic/appointments" element={user ? <MechanicAppointments /> : <Navigate to="/login" />} />
            <Route path="/mechanic/appointment/:appointmentId/add-service" element={user ? <MechanicAddService /> : <Navigate to="/login" />} />
            <Route path="/mechanic/billing" element={user ? <MechanicBilling /> : <Navigate to="/login" />} />
            <Route path="/mechanic/service-history" element={user ? <MechanicServiceHistory /> : <Navigate to="/login" />} />
            <Route path="/mechanic/:id"         element={user ? <MechanicProfile />    : <Navigate to="/login" />} />

            {/* COMPARTIDAS */}
            <Route path="/mechanics"                          element={user ? <MechanicsList />      : <Navigate to="/login" />} />
            <Route path="/mechanics/map"                      element={user ? <MechanicsMap />       : <Navigate to="/login" />} />
            <Route path="/asistencia"                         element={user ? <Asistencia />         : <Navigate to="/login" />} />
            <Route path="/emergency-request"                  element={user ? (EMERGENCIAS_ACTIVAS ? <EmergencyRequest /> : <Navigate to="/asistencia" replace />) : <Navigate to="/login" />} />
            <Route path="/provider/mode"                      element={user ? (EMERGENCIAS_ACTIVAS ? <ProviderMode /> : <Navigate to="/home" replace />) : <Navigate to="/login" />} />
            <Route path="/appointments"                       element={user ? <MyAppointments />     : <Navigate to="/login" />} />
            <Route path="/appointment/request/:mechanicId"    element={user ? <AppointmentRequest /> : <LoginVolviendoAqui />} />

            {/* TALLER */}
            {/* /workshop/setup queda SIN guard de rol a propósito: es el alta de
                negocios. `createWorkshop` asciende a workshop_owner a quien la
                llame (functions/src/subscriptions.js), y Pricing.js manda ahí a
                cualquiera desde una página pública. Cerrarla dejaría a la app sin
                forma de registrar un taller nuevo. */}
            <Route path="/workshop/setup"        element={user ? <WorkshopSetup />       : <Navigate to="/login" />} />
            <Route path="/workshop/dashboard"    element={user ? <RequireRole roles={WORKSHOP_ROLES}><WorkshopDashboard /></RequireRole>   : <Navigate to="/login" />} />
            <Route path="/workshop/appointments" element={user ? <RequireRole roles={WORKSHOP_ROLES}><WorkshopAppointments /></RequireRole> : <Navigate to="/login" />} />
            <Route path="/workshop/reviews"      element={user ? <RequireRole roles={WORKSHOP_ROLES}><WorkshopReviews /></RequireRole>     : <Navigate to="/login" />} />
            <Route path="/workshop/store"        element={user ? <RequireRole roles={WORKSHOP_ROLES}><WorkshopStore /></RequireRole>       : <Navigate to="/login" />} />
            <Route path="/workshop/products"     element={user ? <RequireRole roles={WORKSHOP_ROLES}><WorkshopProducts /></RequireRole>    : <Navigate to="/login" />} />
            <Route path="/workshop/orders"       element={user ? <RequireRole roles={WORKSHOP_ROLES}><WorkshopOrders /></RequireRole>      : <Navigate to="/login" />} />
            <Route path="/workshop/earnings"     element={user ? <RequireRole roles={WORKSHOP_ROLES}><WorkshopEarnings /></RequireRole>    : <Navigate to="/login" />} />
            <Route path="/workshop/profile-views" element={user ? <RequireRole roles={WORKSHOP_ROLES}><WorkshopProfileViews /></RequireRole> : <Navigate to="/login" />} />
            <Route path="/workshop/billing"       element={user ? <RequireRole roles={WORKSHOP_ROLES}><WorkshopBilling /></RequireRole>    : <Navigate to="/login" />} />
            <Route path="/workshop/clients"      element={user ? <RequireRole roles={WORKSHOP_ROLES}><WorkshopClients /></RequireRole>    : <Navigate to="/login" />} />
            <Route path="/workshop/calendar"     element={user ? <RequireRole roles={WORKSHOP_ROLES}><WorkshopCalendar /></RequireRole>   : <Navigate to="/login" />} />
            <Route path="/workshop/crm"          element={user ? <RequireRole roles={WORKSHOP_ROLES}><WorkshopCrm /></RequireRole>        : <Navigate to="/login" />} />
            <Route path="/workshop/verification" element={user ? <RequireRole roles={WORKSHOP_ROLES}><WorkshopVerification /></RequireRole> : <Navigate to="/login" />} />

            {/* MARKETPLACE */}
            <Route path="/marketplace" element={user ? <Marketplace />  : <Navigate to="/login" />} />
            <Route path="/product/:id" element={user ? <ProductDetail /> : <Navigate to="/login" />} />
            <Route path="/cart"        element={user ? <Cart />         : <Navigate to="/login" />} />
            <Route path="/checkout"    element={user ? <Checkout /> : <Navigate to="/login" />} />
            <Route path="/my-orders"   element={user ? <MyOrders />     : <Navigate to="/login" />} />

            {/* AUTOS EN VENTA (clasificados de la comunidad) */}
            <Route path="/autos"                    element={user ? <AutosList />   : <Navigate to="/login" />} />
            <Route path="/autos/publicar"           element={user ? <SellWizard />  : <Navigate to="/login" />} />
            <Route path="/autos/publicar/:vehicleId" element={user ? <SellWizard /> : <Navigate to="/login" />} />
            <Route path="/autos/verificar/:inspectionId" element={user ? <InspectionForm /> : <Navigate to="/login" />} />
            <Route path="/autos/:id"                element={user ? <AutoDetail />  : <Navigate to="/login" />} />
            <Route path="/mis-anuncios"             element={user ? <MyListings />  : <Navigate to="/login" />} />
            <Route path="/workshop/verifications"   element={user ? <WorkshopInspections /> : <Navigate to="/login" />} />
            <Route path="/workshop/register-service" element={user ? <WorkshopRegisterService /> : <Navigate to="/login" />} />

            {/* MENSAJERÍA */}
            <Route path="/messages"                 element={user ? <Messages />         : <Navigate to="/login" />} />
            <Route path="/messages/:conversationId" element={user ? <ChatConversation /> : <Navigate to="/login" />} />

            {/* AYUDA Y SOPORTE */}
            <Route path="/help"          element={user ? <Help />          : <Navigate to="/login" />} />
            <Route path="/help/case/:id" element={user ? <HelpCase />      : <Navigate to="/login" />} />
            <Route path="/sugerencias"   element={user ? <Suggestions />   : <Navigate to="/login" />} />
            <Route path="/my-cases"      element={user ? <BusinessCases /> : <Navigate to="/login" />} />

            {/* ADMIN */}
            <Route path="/admin/dashboard" element={user ? <RequireAdmin><AdminDashboard /></RequireAdmin> : <Navigate to="/login" />} />
            <Route path="/admin/user/:id"  element={user ? <RequireAdmin><AdminUserDetail /></RequireAdmin> : <Navigate to="/login" />} />
            <Route path="/admin/kyc"       element={user ? <RequireAdmin><AdminKyc /></RequireAdmin> : <Navigate to="/login" />} />
            <Route path="/admin/launch"    element={user ? <RequireAdmin><AdminLaunch /></RequireAdmin> : <Navigate to="/login" />} />
            <Route path="/admin/reviews"   element={user ? <RequireCapability capability="workshops.moderate"><AdminReviews /></RequireCapability> : <Navigate to="/login" />} />
            <Route path="/admin/audit"     element={user ? <RequireAdmin><AdminAudit /></RequireAdmin> : <Navigate to="/login" />} />
            <Route path="/admin/census"    element={user ? <RequireAdmin><AdminCensus /></RequireAdmin> : <Navigate to="/login" />} />
            <Route path="/admin/migraciones" element={user ? <RequireAdmin><AdminMigraciones /></RequireAdmin> : <Navigate to="/login" />} />
            <Route path="/admin/emergencies" element={user ? <RequireAdmin><AdminEmergencies /></RequireAdmin> : <Navigate to="/login" />} />
            <Route path="/admin/orders"    element={user ? <RequireAdmin><AdminOrders /></RequireAdmin> : <Navigate to="/login" />} />
            <Route path="/admin/support"   element={user ? <RequireAdmin><AdminSupport /></RequireAdmin> : <Navigate to="/login" />} />
            <Route path="/admin/sugerencias" element={user ? <RequireAdmin><AdminSuggestions /></RequireAdmin> : <Navigate to="/login" />} />
            <Route path="/admin/analytics" element={user ? <RequireAdmin><AdminAnalytics /></RequireAdmin> : <Navigate to="/login" />} />
            <Route path="/admin/products"  element={user ? <RequireAdmin><AdminProductsMod /></RequireAdmin> : <Navigate to="/login" />} />
            <Route path="/admin/announcements" element={user ? <RequireAdmin><AdminAnnouncements /></RequireAdmin> : <Navigate to="/login" />} />
            <Route path="/admin/workshop-kyc" element={user ? <RequireCapability capability="workshops.approve"><AdminWorkshopKyc /></RequireCapability> : <Navigate to="/login" />} />
            {/* ╔═══════════════════════════════════════════════════════════════╗
                ║ LAS DOS PUERTAS DE STAFF. NO SON <RequireAdmin>.              ║
                ╚═══════════════════════════════════════════════════════════════╝
                Un encargado es CLIENT en su cuenta: lo que le abre estas dos
                colas es una CAPACIDAD suelta. El admin pasa igual, sin que se le
                conceda nada. Todo lo demas de /admin/* sigue siendo del dueno,
                empezando por el panel, donde viven el padron y las cedulas. */}
            <Route path="/admin/workshop-approvals" element={user ? <RequireCapability capability="workshops.approve"><AdminWorkshopApprovals /></RequireCapability> : <Navigate to="/login" />} />
            <Route path="/admin/appeals"   element={user ? <RequireCapability capability="appeals.review"><AdminAppeals /></RequireCapability> : <Navigate to="/login" />} />

            {/* PÚBLICO */}
            <Route path="/privacy"                   element={<Privacy />} />
            <Route path="/terms"                     element={<Terms />} />
            <Route path="/payment-security"          element={<PaymentSecurity />} />
            <Route path="/refunds"                   element={<Refunds />} />
            <Route path="/delivery"                  element={<Delivery />} />
            <Route path="/contact"                   element={<Contact />} />
            <Route path="/rankings"                  element={<Rankings />} />
            <Route path="/pricing"                   element={<Pricing />} />
            <Route path="/workshop/profile/:wsDocId" element={<WorkshopProfile />} />
            <Route path="*"                          element={<NotFound />} />
          </Routes>
          </div>
        </Suspense>
        </ErrorBoundary>
      </AuthDesktopLayout>
      {user && <MobileTopBar />}
      {user && <BottomNav />}
      {user && <PushPrompt />}
      {user && <VerifyEmailBanner />}
      {user && <AssistantFab />}
    </div>
    </ProfileGate>
    </TwoFactorGate>
    </LaunchGate>
    </SuspensionGate>
  );
}

function App() {
  return (
    // Boundary raíz: el de dentro de AuthDesktopLayout solo cubre la página. Los
    // gates (Suspension/Launch/TwoFactor) y el chrome (Sidebar, BottomNav,
    // MobileTopBar, PushPrompt, VerifyEmailBanner, AssistantFab...) quedaban fuera
    // de todo boundary, y Sentry no captura errores de render sin uno: un fallo ahí
    // daba pantalla en blanco y cero reporte.
    <ErrorBoundary>
      <ThemeProvider>
        <CartProvider>
          <BrowserRouter>
            <AuthProvider>
              <ToastProvider>
                <TopBarProvider>
                  <AppRoutes />
                </TopBarProvider>
              </ToastProvider>
            </AuthProvider>
          </BrowserRouter>
        </CartProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
